import React, { useEffect, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { FileSpreadsheet, Printer, CalendarRange, Save, Ban, Mail, RefreshCw, Archive, Download, Trash2, FilePlus2, BellRing, ChevronDown } from "lucide-react";

const CADENCES = [
  { v: "suppress", l: "Suppress", icon: Ban },
  { v: "monthly", l: "Create monthly" },
  { v: "bi", l: "Every 2 months" },
  { v: "quarterly", l: "Quarterly" },
];
const FOCUS_TABS = [
  ["all", "All"], ["transactions", "Transactions"], ["benefits", "Benefits"], ["alerts", "Alerts"],
  ["inhouse", "In-House charge"], ["bank", "Bank charges"], ["tax", "Tax"],
];
const CHARGE_FOCUS = ["inhouse", "bank", "tax"];
const RANGE_PRESETS = [["15", "15 days"], ["30", "30 days"], ["60", "60 days"], ["90", "90 days"], ["365", "1 year"], ["custom", "Custom"]];

const iso = (d) => d.toISOString().slice(0, 10);
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const daysAgo = (n) => { const f = new Date(); f.setDate(f.getDate() - n); return iso(f); };

function monthOptions(n = 15) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const frm = iso(new Date(d.getFullYear(), d.getMonth(), 1));
    const to = iso(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    out.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: `${TL(MONTHS[d.getMonth()])} ${d.getFullYear()}`, frm, to });
  }
  return out;
}

// Statement-alerts control (top-of-Currencida only): all-on/all-off/select toggle + a per-account
// dropdown with a single alert checkbox each. Suppressed accounts are greyed/unfillable.
export function StatementAlerts() {
  const [accts, setAccts] = useState([]);
  const [sort, setSort] = useState("alpha");
  const [open, setOpen] = useState(false);

  const load = (s = sort) => client.get("/currencida/statement/accounts", { params: { sort: s } })
    .then((r) => setAccts(r.data.accounts || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort]);

  const eligible = accts.filter((a) => !a.suppressed);
  const onCount = eligible.filter((a) => a.alert_enabled).length;
  const allOn = eligible.length > 0 && onCount === eligible.length;
  const allOff = onCount === 0;

  const toggleOne = async (a, enabled) => {
    if (a.suppressed) return;
    setAccts((xs) => xs.map((x) => (x.id === a.id ? { ...x, alert_enabled: enabled } : x)));
    try { await client.put("/currencida/statement/alert", { account_id: a.id, enabled }); }
    catch { load(); }
  };
  const bulk = async (mode) => {
    try { await client.post("/currencida/statement/alerts/bulk", { mode }); load(); }
    catch { /* noop */ }
  };

  const pill = (active) => `rounded-full px-3 py-1 text-xs font-medium transition ${active ? "bg-[var(--primary)] text-white" : "border border-black/15 hover:bg-black/5 dark:border-white/20 dark:hover:bg-white/10"}`;

  return (
    <div className="relative flex flex-col items-center" data-testid="statement-alerts">
      <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide opacity-55"><BellRing size={12} /> {TL("Statement alerts")}</div>
      <div className="flex items-center gap-1.5" data-testid="alert-toggle">
        <button onClick={() => bulk("on")} data-testid="alerts-all-on" className={pill(allOn)}>{TL("All on")}</button>
        <button onClick={() => bulk("off")} data-testid="alerts-all-off" className={pill(allOff)}>{TL("All off")}</button>
        <button onClick={() => setOpen((o) => !o)} data-testid="alerts-select" className={pill(!allOn && !allOff)}>{TL("Select")}</button>
      </div>
      {open && (
        <div className="absolute top-full z-30 mt-1 max-h-72 w-72 overflow-auto rounded-lg border border-black/15 bg-white p-1 shadow-xl dark:border-white/20 dark:bg-neutral-900" data-testid="alert-accounts-list">
          {accts.length === 0 && <div className="px-3 py-2 text-xs opacity-60">{TL("No active accounts.")}</div>}
          {accts.map((a) => (
            <label key={a.id} data-testid={`alert-acct-${a.id}`}
              className={`flex items-center gap-2 rounded px-2 py-1.5 text-xs ${a.suppressed ? "cursor-not-allowed opacity-45" : "cursor-pointer hover:bg-black/5 dark:hover:bg-white/10"}`}>
              <input type="checkbox" checked={a.alert_enabled} disabled={a.suppressed}
                onChange={(e) => toggleOne(a, e.target.checked)} data-testid={`alert-check-${a.id}`}
                className="h-4 w-4 disabled:cursor-not-allowed" />
              <span className="flex-1 truncate">{a.name}</span>
              {a.suppressed && <span className="text-[10px] italic opacity-70">{TL("suppressed")}</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}


// scope: "all" (top-of-Currencida) or a currency account id (per-account, embedded in its register)
export default function CurrencidaStatements({ scope = "all", accountName }) {
  const [cfg, setCfg] = useState({ frequency: "monthly", statement_date: 1, email_enabled: false, recipient: "" });
  const [msg, setMsg] = useState("");
  const [months] = useState(() => monthOptions(15));
  const [month, setMonth] = useState(() => monthOptions(1)[0].key);
  const [preset, setPreset] = useState("30");
  const [from, setFrom] = useState(() => daysAgo(30));
  const [to, setTo] = useState(() => iso(new Date()));
  const [focus, setFocus] = useState("all");
  const SORT_BASE = [["", "Sort field…"], ["code", "Client code"], ["line1", "Line 1 Note"], ["interim", "Interim"], ["misc", "Miscellaneous"]];
  const [sortField, setSortField] = useState("");
  const [customSorts, setCustomSorts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fopfo_custom_fields") || "[]"); } catch { return []; }
  });
  const [counterparty, setCounterparty] = useState("");
  const [cpList, setCpList] = useState([]);
  const [archive, setArchive] = useState([]);
  const PREF_KEY = `fopfo_stmt_open_${scope}`;
  const functionalOK = () => !!(window.__cookieConsent && window.__cookieConsent.functional);
  const [open, setOpen] = useState(() => {
    try {
      if (functionalOK()) {
        const v = localStorage.getItem(PREF_KEY);
        if (v === "1") return true;
        if (v === "0") return false;
      }
    } catch (e) { /* noop */ }
    return scope === "all";
  });
  const toggleOpen = () => setOpen((o) => {
    const next = !o;
    try { if (functionalOK()) localStorage.setItem(PREF_KEY, next ? "1" : "0"); } catch (e) { /* noop */ }
    return next;
  });
  const collapseAll = (e) => {
    if (e) e.stopPropagation();
    try {
      if (functionalOK()) {
        Object.keys(localStorage).filter((k) => k.startsWith("fopfo_stmt_open_")).forEach((k) => localStorage.setItem(k, "0"));
      }
    } catch (err) { /* noop */ }
    window.dispatchEvent(new CustomEvent("fopfo-collapse-statements"));
  };
  useEffect(() => {
    const h = () => {
      setOpen(false);
      try { if (functionalOK()) localStorage.setItem(PREF_KEY, "0"); } catch (e) { /* noop */ }
    };
    window.addEventListener("fopfo-collapse-statements", h);
    return () => window.removeEventListener("fopfo-collapse-statements", h);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    client.get("/currencida/statement/config", { params: { scope } }).then((r) => setCfg(r.data)).catch(() => {});
    client.get("/currencida/statement/counterparties", { params: { scope } }).then((r) => setCpList(r.data.counterparties || [])).catch(() => {});
    client.get("/currencida/statement/archive", { params: { scope } }).then((r) => setArchive(r.data.statements || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const saveCfg = async (patch = {}) => {
    const next = { ...cfg, ...patch, scope };
    setCfg(next);
    try { await client.put("/currencida/statement/config", next); setMsg("Schedule saved."); setTimeout(() => setMsg(""), 2500); }
    catch { setMsg("Could not save schedule."); }
  };

  // Left = date range · Right (focus) = what to include in this snapshot.
  const exportParams = (frm, t) => ({
    scope, focus, charge_type: CHARGE_FOCUS.includes(focus) ? focus : "",
    group: sortField, counterparty,
    ...(frm ? { frm } : {}), ...(t ? { to: t } : {}),
  });
  const dl = async (fmt, frm, t) => {
    try {
      const res = await client.get(`/currencida/statement/export.${fmt}`, { params: exportParams(frm, t), responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `currencida_statement.${fmt}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { setMsg("Export failed."); }
  };
  const printPdf = async (frm, t) => {
    const qs = new URLSearchParams(exportParams(frm, t)).toString();
    const res = await client.get(`/currencida/statement/export.pdf?${qs}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const w = window.open(url, "_blank"); if (w) w.onload = () => w.print();
  };

  const generateActual = async () => {
    try {
      await client.post("/currencida/statement/generate", { scope });
      setMsg("Statement generated & saved to archive."); setTimeout(() => setMsg(""), 2500);
      client.get("/currencida/statement/archive", { params: { scope } }).then((r) => setArchive(r.data.statements || [])).catch(() => {});
    } catch { setMsg("Could not generate statement."); }
  };
  const downloadStored = async (sid) => {
    try {
      const res = await client.get(`/currencida/statement/archive/${sid}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `currencida_statement.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { setMsg("Download failed."); }
  };
  const deleteStored = async (sid) => {
    if (!window.confirm("Remove this stored statement?")) return;
    try { await client.delete(`/currencida/statement/archive/${sid}`); setArchive((a) => a.filter((s) => s.id !== sid)); }
    catch { setMsg("Could not delete."); }
  };

  const applyPreset = (v) => {
    setPreset(v);
    if (v !== "custom") { setFrom(daysAgo(Number(v))); setTo(iso(new Date())); }
  };
  const rangeDates = () => [from || undefined, to || undefined];
  const monthDates = () => { const m = months.find((x) => x.key === month); return [m?.frm, m?.to]; };

  const addSortField = () => {
    const v = (window.prompt("Add a sort/label field:") || "").trim();
    if (!v) return;
    let c = []; try { c = JSON.parse(localStorage.getItem("fopfo_custom_fields") || "[]"); } catch { c = []; }
    if (!c.includes(v)) c = [...c, v];
    setCustomSorts(c);
    localStorage.setItem("fopfo_custom_fields", JSON.stringify(c));
    setSortField(v);
  };

  const title = scope === "all" ? TL("Statements — All accounts") : `${TL("Statements —")} ${accountName || TL("this account")}`;

  const pill = (active, danger) =>
    `rounded-full px-3 py-1 text-xs font-medium transition ${active ? (danger ? "bg-[#DA1E28] text-white" : "bg-[var(--primary)] text-white") : "border border-black/15 hover:bg-black/5 dark:border-white/20 dark:hover:bg-white/10"}`;

  return (
    <Card className="p-4" data-testid={`currencida-statements-${scope}`}>
      {/* Header row — clickable collapse/expand toggle */}
      <div className="flex w-full items-center gap-2">
        <button type="button" onClick={toggleOpen}
          className="flex flex-1 items-center gap-2 text-start text-base font-semibold" data-testid={`statements-toggle-${scope}`} aria-expanded={open}>
          <CalendarRange size={17} /> {title}
        </button>
        {scope === "all" && (
          <button type="button" onClick={collapseAll} data-testid="statements-collapse-all"
            className="rounded-md border border-black/15 px-2 py-1 text-xs font-medium hover:bg-black/5 dark:border-white/20 dark:hover:bg-white/10">
            {TL("Collapse all")}
          </button>
        )}
        <button type="button" onClick={toggleOpen} aria-label={open ? "Collapse" : "Expand"} data-testid={`statements-chevron-${scope}`}>
          <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {!open && <div className="mt-1 text-xs opacity-55" data-testid={`statements-collapsed-hint-${scope}`}>{TL("Click to view statements, alerts & schedule")}</div>}

      {open && (<div className="mt-3">
      {/* STRIP 1 — Create Range (left) · Alerts (centre) · Select/Sort focus tabs (right) */}
      <div className="mb-3 flex flex-wrap items-start gap-x-6 gap-y-3 rounded-lg border border-black/10 px-4 py-3 dark:border-white/10">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-1.5" data-testid="range-presets">
            <span className="me-1 text-[11px] font-semibold uppercase tracking-wide opacity-55">{TL("Ad Hoc Statement")}</span>
            {RANGE_PRESETS.map(([v, l]) => (
              <button key={v} onClick={() => applyPreset(v)} data-testid={`range-${v}`} className={pill(preset === v)}>{TL(l)}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="range-dates">
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} data-testid="range-from" className="h-8 w-36" />
            <span className="opacity-50">→</span>
            <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} data-testid="range-to" className="h-8 w-36" />
            <Button size="sm" variant="outline" onClick={() => printPdf(...rangeDates())} data-testid="print-range"><Printer size={14} /> {TL("Print/Export range")}</Button>
          </div>
        </div>
        <div className="mx-auto"><StatementAlerts /></div>
        <div className="max-w-md">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-55">{TL("Ad Hoc Select/Sort")} <span className="ms-1 font-normal opacity-60">{TL("(focus of snapshot)")}</span></div>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="focus-tabs">
            {FOCUS_TABS.map(([v, l]) => (
              <button key={v} onClick={() => setFocus(v)} data-testid={`focus-${v}`} className={pill(focus === v)}>{TL(l)}</button>
            ))}
            <select value={sortField}
              onChange={(e) => { e.target.value === "__add" ? addSortField() : setSortField(e.target.value); }}
              data-testid="sort-field" className="h-7 rounded-md border border-black/15 bg-transparent px-2 text-xs dark:border-white/20">
              {SORT_BASE.map(([v, l]) => <option key={v} value={v}>{TL(l)}</option>)}
              {customSorts.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="__add">{TL("➕ Add field…")}</option>
            </select>
            <select value={counterparty} onChange={(e) => setCounterparty(e.target.value)}
              data-testid="counterparty-select" className="h-7 rounded-md border border-black/15 bg-transparent px-2 text-xs dark:border-white/20">
              <option value="">{TL("Counterparty/Vendor…")}</option>
              {cpList.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* STRIP 2 — Scheduled statement */}
      <div className="rounded-lg border border-black/10 px-4 py-3 dark:border-white/10">
        <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="cadence-buttons">
          <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-55"><Mail size={13} /> {TL("Scheduled statement")}</span>
          {CADENCES.map(({ v, l, icon: Icon }) => (
            <button key={v} onClick={() => saveCfg({ frequency: v })} data-testid={`cadence-${v}`} className={`inline-flex items-center gap-1 ${pill(cfg.frequency === v, v === "suppress")}`}>
              {Icon && <Icon size={12} />}{TL(l)}
            </button>
          ))}
          <label className="flex items-center gap-1.5 text-xs opacity-80">
            <span className="opacity-70">{TL("Print month:")}</span>
            <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="month-picker"
              className="h-8 rounded-md border border-black/15 bg-transparent px-2 text-xs dark:border-white/20">
              {months.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </label>
          <div className="ms-auto flex flex-wrap items-center gap-1.5" data-testid="statement-actions">
            <Button size="sm" onClick={() => printPdf(...monthDates())} data-testid="print-statement"><Printer size={14} /> {TL("Print/Export statement")}</Button>
            <Button size="sm" variant="outline" onClick={() => dl("csv", ...rangeDates())} data-testid="hdr-csv"><FileSpreadsheet size={14} /> {TL("CSV")}</Button>
            <Button size="sm" variant="ghost" onClick={load} data-testid="hdr-refresh"><RefreshCw size={14} /> {TL("Refresh")}</Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="min-w-52 flex-1">
            <Input type="email" value={cfg.recipient} onChange={(e) => setCfg({ ...cfg, recipient: e.target.value })}
              data-testid="recipient" placeholder={TL("owner@familyoffice.com")} className="h-9" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={cfg.email_enabled} onChange={(e) => saveCfg({ email_enabled: e.target.checked })} data-testid="email-auto" className="h-4 w-4" />
            {TL("Email automatically")}
          </label>
          <label className="flex items-center gap-1.5 text-sm">
            <span className="opacity-70">{TL("Statement Date")}</span>
            <Input type="number" min="1" max="28" value={cfg.statement_date}
              onChange={(e) => setCfg({ ...cfg, statement_date: e.target.value })} data-testid="statement-date" className="h-9 w-16" />
          </label>
          <Button size="sm" variant="outline" onClick={() => saveCfg()} data-testid="save-schedule"><Save size={14} /> {TL("Save schedule")}</Button>
          <Button size="sm" onClick={generateActual} data-testid="generate-now"><FilePlus2 size={14} /> {TL("Generate statement now")}</Button>
        </div>
      </div>

      {/* ARCHIVE — stored snapshots (up to 15 months back; no need to print monthly) */}
      <div className="mt-3 rounded-lg border border-black/10 px-4 py-3 dark:border-white/10" data-testid="statement-archive">
        <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide opacity-55">
          <Archive size={13} /> {TL("Stored statements")} <span className="font-normal opacity-60">{TL("(kept 15 months)")}</span>
        </div>
        {archive.length === 0 ? (
          <div className="py-2 text-sm opacity-60" data-testid="archive-empty">{TL("No saved statements yet. Only actual statements on your Statement Date (or “Generate statement now”) are saved here — ad-hoc range/selection prints are not archived.")}</div>
        ) : (
          <div className="divide-y divide-black/5 dark:divide-white/10">
            {archive.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm" data-testid={`archive-row-${s.id}`}>
                <span className="font-medium">{s.frm || TL("beginning")} → {s.to || TL("today")}</span>
                <span className="opacity-60">{TL(s.scope_label)}</span>
                {s.frequency && <span className="rounded-full bg-black/5 px-2 py-0.5 text-xs dark:bg-white/10">{TL(s.frequency)}</span>}
                <span className="opacity-60">{s.entry_count} {TL("entr(ies)")}</span>
                <span className="ms-auto flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => downloadStored(s.id)} data-testid={`archive-dl-${s.id}`}><Download size={13} /> {TL("PDF")}</Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteStored(s.id)} data-testid={`archive-del-${s.id}`}><Trash2 size={13} /></Button>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {msg && <div className="mt-2 text-sm font-medium text-[var(--primary)]" data-testid="statements-msg">{msg}</div>}
      </div>)}
    </Card>
  );
}
